import {
  DownloadOutlined,
  FileTextOutlined,
  LoadingOutlined,
} from '@ant-design/icons'
import { Horizontal, Texto } from '@gravitate-js/excalibrr'
import { Button, theme } from 'antd'
import { useState } from 'react'
import { fetchArtifact } from './artifactFetch'

export type AttachmentChipProps = {
  taskId: string
  filename: string
  mimeType: string
  sizeBytes: number
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// A short type label + accent color derived from the file extension, falling
// back to the mime type's subtype. Drives the file-glyph badge below.
function fileType(
  filename: string,
  mimeType: string,
): { label: string; color: string } {
  const ext = filename.includes('.')
    ? filename.split('.').pop()?.toUpperCase()
    : undefined
  const label = ext ?? mimeType.split('/')[1]?.toUpperCase() ?? 'FILE'
  let color = '#64748b' // slate — neutral default
  if (/^(CSV|XLS|XLSX|TSV)$/.test(label))
    color = '#15803d' // spreadsheet green
  else if (label === 'PDF') color = '#b91c1c'
  else if (mimeType.startsWith('image/')) color = '#7c3aed'
  else if (/^(JSON|TXT|XML|LOG)$/.test(label)) color = '#2563eb'
  return { label: label.slice(0, 4), color }
}

// Document glyph with a folded corner and a colored band carrying the type
// label — the standard "file of type X" visual, drawn inline so we don't need
// per-mime icon assets.
function FileTypeIcon({ label, color }: { label: string; color: string }) {
  return (
    <svg
      width={30}
      height={38}
      viewBox='0 0 32 40'
      fill='none'
      role='presentation'
    >
      <path
        d='M5 2.5h14L27 9.5V36a1.5 1.5 0 0 1-1.5 1.5H5A1.5 1.5 0 0 1 3.5 36V4A1.5 1.5 0 0 1 5 2.5Z'
        fill='#fff'
        stroke='#cdd5df'
        strokeWidth={1.4}
      />
      <path
        d='M19 2.5V8a1.5 1.5 0 0 0 1.5 1.5H27'
        fill='#eef1f5'
        stroke='#cdd5df'
        strokeWidth={1.4}
        strokeLinejoin='round'
      />
      <rect x={4} y={22} width={22} height={11} rx={2} fill={color} />
      <text
        x={15}
        y={30.2}
        textAnchor='middle'
        fontSize={7}
        fontWeight={700}
        fill='#fff'
        fontFamily='sans-serif'
      >
        {label}
      </text>
    </svg>
  )
}

export function AttachmentChip({
  taskId,
  filename,
  mimeType,
  sizeBytes,
}: AttachmentChipProps) {
  const { token } = theme.useToken()
  const [expired, setExpired] = useState(false)
  const [failed, setFailed] = useState(false)
  const [downloading, setDownloading] = useState(false)

  const { label, color } = fileType(filename, mimeType)

  async function handleClick() {
    if (expired || downloading) return
    setDownloading(true)
    setFailed(false)
    try {
      const resp = await fetchArtifact(taskId, filename)
      if (resp.status === 410) {
        setExpired(true)
        return
      }
      if (!resp.ok) {
        setFailed(true)
        return
      }
      const blob = await resp.blob()
      const objectUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objectUrl
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
    } catch {
      setFailed(true)
    } finally {
      setDownloading(false)
    }
  }

  let meta: string
  if (expired) meta = 'Expired'
  else if (failed) meta = 'Download failed — tap to retry'
  else meta = `${humanSize(sizeBytes)} · ${label}`

  return (
    // Plain div (not excalibrr Vertical) — excalibrr containers force
    // overflow:hidden, which clips the download button at the card edge.
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: 10,
        padding: 10,
        background: token.colorBgContainer,
        width: '100%',
        boxSizing: 'border-box',
        opacity: expired ? 0.6 : 1,
      }}
    >
      <Horizontal gap={6} verticalCenter>
        <FileTextOutlined
          style={{ color: token.colorTextTertiary, fontSize: 13 }}
        />
        <Texto
          weight='bold'
          style={{ fontSize: 12, color: token.colorTextSecondary }}
        >
          File ready
        </Texto>
      </Horizontal>

      {/* Plain flexbox: excalibrr's Horizontal/Vertical force overflow:hidden,
          which clips the download button and breaks filename ellipsis. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          width: '100%',
          minWidth: 0,
        }}
      >
        <div style={{ flexShrink: 0, lineHeight: 0 }}>
          <FileTypeIcon label={label} color={color} />
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            flex: '1 1 0',
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          <Texto
            style={{
              fontFamily: 'monospace',
              fontSize: 13,
              color: token.colorText,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block',
            }}
          >
            {filename}
          </Texto>
          <Texto
            appearance='hint'
            style={{
              fontSize: 11,
              color: failed ? token.colorError : undefined,
            }}
          >
            {meta}
          </Texto>
        </div>

        <Button
          type='primary'
          shape='circle'
          size='small'
          aria-label={`Download ${filename}`}
          disabled={expired}
          icon={downloading ? <LoadingOutlined /> : <DownloadOutlined />}
          onClick={handleClick}
          style={{ flexShrink: 0 }}
        />
      </div>
    </div>
  )
}
